/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { motion } from "motion/react";
import { useQuery } from "@tanstack/react-query";
import { getMonitorAgents, MonitorAgent } from "@/api/monitor";
import { getDockerHosts, DockerHost } from "@/api/docker";
import { getSNMPDevices, SNMPDevice } from "@/api/snmp";
import { AgentResourcesWidget } from "@/components/kiosk/widgets/AgentResourcesWidget";
import { DockerHostWidget } from "@/components/kiosk/widgets/DockerHostWidget";
import { SwitchPortsWidget } from "@/components/kiosk/widgets/SwitchPortsWidget";
import { SIZE_COL_SPAN } from "@/constants/gridSizes";

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// Dashboard-level summary pulling in every monitored source (Agents, Docker
// hosts, SNMP switches) so the landing page reads as "all server metrics",
// not just speed test results. Reuses the same compact widgets built for
// kiosk mode instead of duplicating their loading-guard/hook logic.
export const ServerOverview: React.FC = () => {
  const { data: agents = [] } = useQuery<MonitorAgent[]>({
    queryKey: ["monitor-agents"],
    queryFn: getMonitorAgents,
  });
  const { data: dockerHosts = [] } = useQuery<DockerHost[]>({
    queryKey: ["docker-hosts"],
    queryFn: getDockerHosts,
  });
  const { data: snmpDevices = [] } = useQuery<SNMPDevice[]>({
    queryKey: ["snmp-devices"],
    queryFn: getSNMPDevices,
  });

  const enabledAgents = agents.filter((agent) => agent.enabled);
  const enabledDockerHosts = dockerHosts.filter((host) => host.enabled);
  const enabledSnmpDevices = snmpDevices.filter((device) => device.enabled);

  if (
    enabledAgents.length === 0 &&
    enabledDockerHosts.length === 0 &&
    enabledSnmpDevices.length === 0
  ) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
      className="mb-6"
    >
      <h2 className="text-gray-900 dark:text-white text-xl ml-1 font-semibold mb-4">
        Server Overview
      </h2>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-12 gap-4"
      >
        {enabledAgents.map((agent) => (
          <motion.div key={`agent-${agent.id}`} variants={itemVariants} className={SIZE_COL_SPAN.md}>
            <AgentResourcesWidget agentId={agent.id} />
          </motion.div>
        ))}
        {enabledDockerHosts.map((host) => (
          <motion.div key={`docker-${host.id}`} variants={itemVariants} className={SIZE_COL_SPAN.md}>
            <DockerHostWidget hostId={host.id} />
          </motion.div>
        ))}
        {enabledSnmpDevices.map((device) => (
          <motion.div key={`switch-${device.id}`} variants={itemVariants} className={SIZE_COL_SPAN.md}>
            <SwitchPortsWidget deviceId={device.id} />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};
